import { AdsProvider, NoopAdsProvider, configureAds, getAds } from './ads';
import { MONETIZATION, isMonetizationActive } from './config';
import { ConsentProvider, configureConsent, getConsent } from './consent';
import { trackEvent } from './events';
import { IapProvider, NoopIapProvider, configureIap, getIap } from './iap';

async function createAdsProvider(): Promise<AdsProvider> {
  switch (MONETIZATION.adsProvider) {
    case 'demo': {
      const { DemoAdsProvider } = await import('./providers/demo-ads');
      return new DemoAdsProvider();
    }
    case 'web': {
      const { WebAdsProvider } = await import('./providers/web-ads');
      return new WebAdsProvider();
    }
    case 'admob': {
      const { AdmobAdsProvider } = await import('./providers/admob-ads');
      return new AdmobAdsProvider();
    }
    default:
      return new NoopAdsProvider();
  }
}

async function createIapProvider(): Promise<IapProvider> {
  switch (MONETIZATION.iapProvider) {
    case 'demo': {
      const { DemoIapProvider } = await import('./providers/demo-iap');
      return new DemoIapProvider();
    }
    case 'store': {
      const { StoreIapProvider } = await import('./providers/store-iap');
      return new StoreIapProvider();
    }
    default:
      return new NoopIapProvider();
  }
}

async function createConsentProvider(): Promise<ConsentProvider | null> {
  if (MONETIZATION.adsProvider !== 'admob') {
    return null;
  }
  const { AdmobConsentProvider } = await import('./providers/admob-consent');
  return new AdmobConsentProvider();
}

export async function setupMonetization(): Promise<void> {
  if (!isMonetizationActive()) {
    return;
  }
  try {
    const consent = await createConsentProvider();
    if (consent) {
      configureConsent(consent);
    }
    configureAds(await createAdsProvider());
    configureIap(await createIapProvider());
    await getConsent().initialize();
    await getAds().initialize();
    await getIap().initialize();
    trackEvent('monetization_init', {
      ads: MONETIZATION.adsProvider,
      iap: MONETIZATION.iapProvider,
      testing: MONETIZATION.testingAds,
    });
  } catch {
    configureAds(new NoopAdsProvider());
    configureIap(new NoopIapProvider());
  }
}
